import api from '@/api/axiosApi'
import type { ExportRead } from '@/types'
import type { CreateExportResponse } from '@/hooks/useExport'

export interface CommitmentLetterContact {
  name: string
  email: string
}

/** Fields entered in the commitment letter wizard. Dates are `yyyy-MM-dd`. */
export interface CommitmentLetterInputs {
  due_date: string
  sample_count: number
  sample_due_date: string
  sample_contacts: CommitmentLetterContact[]
  confirmation_contacts: CommitmentLetterContact[]
  signer_names: string[]
  /** `vendor_key` -> selected Strategy Code; omitted vendors use the derived default. */
  strategy_codes: Record<string, string>
}

export interface CommitmentLetterGenerateRequest extends CommitmentLetterInputs {
  line_item_ids: string[]
}

export interface CommitmentLetterPreviewRequest extends CommitmentLetterInputs {
  line_item_ids: string[]
  vendor_key: string
}

export interface CommitmentLetterPreviewResponse {
  content: string
}

export interface TransitCalendarRow {
  event_year: number | null
  store_set: string | null
  imp_dom: string
  event_code: string | null
  origin_code: string
  origin_name: string
  origin_country: string | null
  destination_dc: string
  not_before: string
  not_after: string
  /** Calendar strategy group the dates belong to (e.g. "SBA 3, 4, 7"). */
  strategy: string
}

export interface CommitmentLetterVendor {
  vendor_key: string
  vendor_id: string | null
  vendor_code: string | null
  vendor_name: string | null
  line_item_count: number
  ship_points: string[]
  unmapped_ship_points: string[]
  ports: { code: string; name: string; country: string | null }[]
  ship_window_start: string | null
  ship_window_end: string | null
  /** Strategy Code derived from plan type (Seasonal/Core) and conveyable. */
  default_strategy_code: string | null
  /** Strategy Code used for this vendor's letter (default or planner override). */
  strategy_code: string | null
  /** False when the Supply Chain calendar has no dates for `strategy_code`. */
  strategy_has_calendar: boolean
  /** Calendar strategy groups matched by `strategy_code`. */
  strategies: string[]
  transit_rows: TransitCalendarRow[]
}

export interface CommitmentLetterPrepareResponse {
  defaults: {
    due_date: string | null
    contacts: CommitmentLetterContact[]
  }
  event: {
    id: string
    name: string
    event_code: string | null
    year: number | null
  } | null
  ship_milestone: string | null
  vendors: CommitmentLetterVendor[]
  line_items_without_vendor: string[]
  /** Strategy Codes the event's Supply Chain calendar has dates for. */
  calendar_strategy_codes: string[]
  /** Strategy Code -> calendar strategy-group labels (what the transit table shows). */
  calendar_strategy_labels: Record<string, string[]>
  warnings: string[]
}

const basePath = (planId: string, versionId: string) =>
  `/plans/${planId}/versions/${versionId}/lines/commitment-letters`

/**
 * Group the selection by vendor and compute each vendor's transit calendar.
 * POST /plans/{planId}/versions/{versionId}/lines/commitment-letters/prepare
 */
export async function prepareCommitmentLetter(
  planId: string,
  versionId: string,
  lineItemIds: string[],
  strategyCodes: Record<string, string> = {}
): Promise<CommitmentLetterPrepareResponse> {
  const response = await api.post<CommitmentLetterPrepareResponse>(
    `${basePath(planId, versionId)}/prepare`,
    { line_item_ids: lineItemIds, strategy_codes: strategyCodes }
  )
  return response.data
}

/**
 * Preview one vendor's commitment letter (filled HTML for rendering)
 * POST /plans/{planId}/versions/{versionId}/lines/commitment-letters/preview
 */
export async function previewCommitmentLetter(
  planId: string,
  versionId: string,
  request: CommitmentLetterPreviewRequest
): Promise<CommitmentLetterPreviewResponse> {
  const response = await api.post<CommitmentLetterPreviewResponse>(
    `${basePath(planId, versionId)}/preview`,
    request
  )
  return response.data
}

/**
 * Generate commitment letter export (per vendor: DOCX + units XLSX + transit
 * calendar XLSX, zipped)
 * POST /plans/{planId}/versions/{versionId}/lines/commitment-letters
 */
export async function createCommitmentLetter(
  planId: string,
  versionId: string,
  request: CommitmentLetterGenerateRequest
): Promise<CreateExportResponse> {
  const response = await api.post<CreateExportResponse>(
    basePath(planId, versionId),
    request
  )
  return response.data
}

/**
 * Get commitment letter export status and download URL
 * GET /exports/{exportId}
 */
export async function getCommitmentLetterExport(
  exportId: string
): Promise<ExportRead> {
  const response = await api.get<ExportRead>(`/exports/${exportId}`)
  return response.data
}

// ---------------------------------------------------------------------------
// Bulk: several plans (active versions), one folder per plan in the zip
// ---------------------------------------------------------------------------

/** Max plans per bulk request (mirrors the backend's MAX_BULK_PLANS). */
export const MAX_BULK_PLANS = 50

export interface BulkPlanSelection {
  plan_id: string
  /** `vendor_key` -> selected Strategy Code for this plan. */
  strategy_codes: Record<string, string>
}

export interface BulkPlanPrepare {
  plan_id: string
  plan_name: string
  version_id: string
  /** Line items left out because they have no vendor. */
  skipped_without_vendor: number
  prepare: CommitmentLetterPrepareResponse
}

export interface BulkSkippedPlan {
  plan_id: string
  plan_name: string | null
  reason: string
}

export interface BulkCommitmentLetterPrepareResponse {
  plans: BulkPlanPrepare[]
  skipped_plans: BulkSkippedPlan[]
}

export interface BulkCommitmentLetterGenerateRequest extends Omit<
  CommitmentLetterInputs,
  'strategy_codes'
> {
  plans: BulkPlanSelection[]
}

export interface BulkCommitmentLetterPreviewRequest extends CommitmentLetterInputs {
  plan_id: string
  vendor_key: string
}

/**
 * Wizard data for every selected plan's active version (vendor-bound items only).
 * POST /commitment-letters/bulk/prepare
 */
export async function prepareBulkCommitmentLetters(
  plans: BulkPlanSelection[]
): Promise<BulkCommitmentLetterPrepareResponse> {
  const response = await api.post<BulkCommitmentLetterPrepareResponse>(
    '/commitment-letters/bulk/prepare',
    { plans }
  )
  return response.data
}

/**
 * Preview one plan / vendor letter in the bulk wizard
 * POST /commitment-letters/bulk/preview
 */
export async function previewBulkCommitmentLetter(
  request: BulkCommitmentLetterPreviewRequest
): Promise<CommitmentLetterPreviewResponse> {
  const response = await api.post<CommitmentLetterPreviewResponse>(
    '/commitment-letters/bulk/preview',
    request
  )
  return response.data
}

/**
 * Generate the bulk export: one folder per plan (and per vendor when a plan
 * has several), each with the letter DOCX + units and transit calendar XLSX.
 * POST /commitment-letters/bulk
 */
export async function createBulkCommitmentLetters(
  request: BulkCommitmentLetterGenerateRequest
): Promise<CreateExportResponse> {
  const response = await api.post<CreateExportResponse>(
    '/commitment-letters/bulk',
    request
  )
  return response.data
}
